import React, { useState, useEffect } from "react";
import Header from "@/components/layout/Header";
import AuthForm from "@/components/auth/AuthForm";
import { toast } from "sonner";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import SEO from "@/components/seo/SEO";
import logger from '@/utils/logger';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return_to');
  const paymentCompleted = searchParams.get('payment_completed') === 'true';
  const fromSignup = searchParams.get('fromSignup') === 'true';
  
  // Get the path they were trying to access - fixed to properly handle state
  const locationState =
    (location.state as {
      from?: string | { pathname: string };
      planType?: string;
    }) || {};
  const from =
    returnTo ? `/${returnTo}` : 
    typeof locationState.from === "string"
      ? locationState.from
      : locationState.from?.pathname || "/dashboard";

  logger.info("Login redirect destination:", from);
  
  // Check if the user has completed payment
  useEffect(() => {
    if (paymentCompleted) {
      toast.success(
        "Your subscription was completed successfully! Please log in to access your account.",
        { 
          position: "bottom-right",
          duration: 5000
        }
      );
      
      // Store subscription status in sessionStorage
      try {
        sessionStorage.setItem('petdocument_subscription_status', JSON.stringify({
          hasActiveSubscription: true,
          timestamp: Date.now()
        }));
        logger.info("Stored subscription status in session storage from login page");
      } catch (e) {
        // Ignore sessionStorage errors
      }
    }
  }, [paymentCompleted]);

  const handleLogin = async (data: { email: string; password: string }) => {
    setIsLoading(true);
    setError(undefined);

    logger.info("Attempting login with:", data.email);
    const { error, success } = await signIn(data.email, data.password);

    if (error) {
      logger.info("Login error:", error.message);
      setError(error.message);
      
      // Show different toast messages based on the specific error
      if (error.message.includes("not confirmed")) {
        toast.error("Please confirm your email before logging in.");
      } else if (error.message.includes("Invalid login credentials")) {
        toast.error("Login failed. Please check your credentials.");
      } else {
        toast.error("Login failed: " + error.message);
      }
    } else if (success) {
      // Check sessionStorage for a pre-selected plan from registration
      let redirectToCheckout = false;
      let planForCheckout = null;
      try {
        planForCheckout = sessionStorage.getItem('selectedPlanForCheckout');
        if (planForCheckout) {
          sessionStorage.removeItem('selectedPlanForCheckout'); // Clear it after reading
          redirectToCheckout = true;
          logger.info(`Found planForCheckout in sessionStorage: ${planForCheckout}`);
        }
      } catch (e) {
        logger.error("Error reading selectedPlanForCheckout from sessionStorage:", e);
      }

      let finalRedirectPath = from; // 'from' is already defined based on return_to or default

      if (redirectToCheckout && planForCheckout) {
        finalRedirectPath = `/checkout?plan=${planForCheckout}`;
        toast.success("Logged in successfully! Redirecting to checkout...", {
          position: "bottom-right",
          duration: 5000
        });
      } else {
        // Existing toast logic for general login
        logger.info("Login successful, redirecting to:", finalRedirectPath);
        // Check if we have a payment_completed flag in sessionStorage (this is existing logic)
        let paymentJustCompleted = false;
        try {
          paymentJustCompleted = sessionStorage.getItem('petdocument_payment_completed') === 'true';
          if (paymentJustCompleted) {
            sessionStorage.removeItem('petdocument_payment_completed');
            sessionStorage.setItem('petdocument_subscription_status', JSON.stringify({
              hasActiveSubscription: true,
              timestamp: Date.now()
            }));
            toast.success("Logged in successfully! Your subscription is now active.", {
              position: "bottom-right",
              duration: 5000
            });
          } else {
            toast.success("Logged in successfully!", {
              position: "bottom-right",
              duration: 5000
            });
          }
        } catch (e) {
          toast.success("Logged in successfully!", {
            position: "bottom-right",
            duration: 5000
          });
        }
      }

      // Manually navigate to the determined path
      navigate(finalRedirectPath);
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Login | PetDocument"
        description="Login to your PetDocument account to access your pet records."
      />
      <Header />

      <main className="pt-24 pb-16">
        <div className="container-narrow">
          <div className="max-w-md mx-auto">
            {fromSignup && (
              <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6 rounded-md">
                <p className="font-bold">Email Confirmation Needed</p>
                <p>Please confirm your email before logging in.</p>
              </div>
            )}
            <div className="bg-white rounded-2xl p-8 shadow-soft">
              <AuthForm
                type="login"
                onSubmit={handleLogin}
                isLoading={isLoading}
                error={error}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
